import { useQuery } from '@tanstack/react-query';
import { firstValueFrom } from 'rxjs';

import { useResourceBookingServiceClient } from 'hooks/use-resource-booking-service-client.hook';
import { CoworkerGroupResponse } from 'store/api-client/coworker-types';

// Groups change only through this user's own edits, which invalidate the key; a long stale time keeps
// the chips from refetching on every sheet open.
const STALE_TIME = 5 * 60 * 1000;

const EMPTY: CoworkerGroupResponse[] = [];

export const COWORKER_GROUPS_QUERY_KEY = 'coworker-groups';

interface UseCoworkerGroupsQueryParams {
  /** `useCoworkerVisibility().effectiveTeams` — with teams off the BE answers 403, so do not ask. */
  enabled?: boolean;
}

export interface UseCoworkerGroupsQueryResult {
  groups: CoworkerGroupResponse[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * The requester's coworker groups in the building (§C3): the chips in the find-a-coworker sheet, and
 * what the map resolves an active group against. One key per building — every mutation invalidates it.
 */
export const useCoworkerGroupsQuery = ({
  enabled = true,
}: UseCoworkerGroupsQueryParams = {}): UseCoworkerGroupsQueryResult => {
  const { getClient, buildingUuid } = useResourceBookingServiceClient();

  const isEnabled = enabled && !!buildingUuid;

  const result = useQuery({
    queryKey: [COWORKER_GROUPS_QUERY_KEY, buildingUuid],
    queryFn: () => firstValueFrom(getClient().listCoworkerGroups(buildingUuid as string)),
    enabled: isEnabled,
    staleTime: STALE_TIME,
  });

  return {
    groups: result.data ?? EMPTY,
    isLoading: isEnabled && result.isPending,
    isError: result.isError,
  };
};
